import React, { useEffect, useState } from 'react';
import {
  getTodayDateTimeUTC,
  sendFormSubmissionEmail,
  updateEnterpriseLeadsInGoogleSheet,
  validateEmail,
} from 'src/utils/functions';
import { ToastContainer, toast } from 'react-toastify';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  Modal,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { COLOR_PRIMARY_1, COLOR_PRIMARY_2, SIDEBAR_COLOR } from 'src/colors';
import postOnSlack from 'src/core/postOnSlack';
import { User } from 'src/interfaces/iuser';
import { SCALE_CTA } from 'src/const';

const DemoBookingForm = ({
  matches,
  user,
  showDemoBookingForm,
  closeBookingForm,
  postSubmitRedirection,
  headingFor = 'demo',
}: {
  matches: boolean;
  user: User;
  showDemoBookingForm: boolean;
  closeBookingForm: any;
  postSubmitRedirection?: any;
  headingFor?: string;
}) => {
  const [email, setEmail] = useState<string>(user.email == 'Trial' ? '' : user.email);
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [markets, setMarkets] = useState<string>('');
  const [additionNote, setAdditionalNote] = useState<string>('');
  const [showEmailNotEnteredWarning, setEmailNotEnteredWarning] = useState<boolean>(false);
  const [showEnterValidEmailWarning, setEnterValidEmailWarning] = useState<boolean>(false);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);

  const successAlert = (text: string) =>
    toast.success(text, {
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'colored',
      draggablePercent: 60,
    });

  const headingMapper = {
    sales: 'Talk to Sales',
    demo: `${SCALE_CTA} to unlock this feature today!`,
  };

  const slackChannelMapper = {
    sales: 'C05JDGPMH8T',
    demo: 'C072095QUU9',
  };

  useEffect(() => {
    setEmail(user.email == 'Trial' ? '' : user.email);
  }, [user]);

  const handleOnContactSalesBtn = async () => {
    if (email === 'Trial' || email === '') {
      setEmailNotEnteredWarning(true);
      setEnterValidEmailWarning(false);
    } else {
      if (validateEmail(email)) {
        setSubmitLoading(true);
        setEmailNotEnteredWarning(false);
        setEnterValidEmailWarning(false);
        let message = `Demo Booking Form Submitted from Advance Dashboard\nName: ${firstName +
          ' ' +
          lastName}\nUser: ${email}\nCompany name/Individual use: ${companyName}\nPhone Number: ${phoneNumber}\nMarkets: ${markets}\nAdditional notes: ${additionNote}\n`;
        let sendSlackMessage = await postOnSlack(slackChannelMapper[headingFor], message);
        let name = firstName;
        let emailText = `<b>Hi ${name},</b> <br><br> Thanks for your interest in Airbtics. We’ll review your request and get back to you in 1-2 working days. In the mean time... <br><br> <b>API documentation: </b> <a href="https://documenter.getpostman.com/view/25155751/2s9XxtyvNn">Airbtics Estimator API Documentation</a> <br> <b>API trial: </b><a href="https://rapidapi.com/airbtics-airbtics-default/api/airbnb-income-prediction">Airbnb Income Prediction</a><br><b>Custom Data FAQ: </b><a href="https://airbtics.com/airbnb-data/">FAQ</a> <br><br> Thanks, <br>Airbtics Team`;
        let title = 'Airbtics Demo Booking Confirmation: Your Request Has Been Received ';
        await sendFormSubmissionEmail(email, emailText, title);
        let todaysDate = getTodayDateTimeUTC();
        await updateEnterpriseLeadsInGoogleSheet(
          'demo_booking_request',
          `[${todaysDate}|${name}|${email}|${companyName}|${phoneNumber}|${markets}|${additionNote}]`
        );
        setSubmitLoading(false);
        closeBookingForm();
        successAlert('Form submitted successfully');
        postSubmitRedirection &&
          setTimeout(() => {
            postSubmitRedirection();
          }, 3000);
      } else {
        setEmailNotEnteredWarning(false);
        setEnterValidEmailWarning(true);
      }
    }
  };

  return (
    <Modal
      open={showDemoBookingForm}
      onClose={closeBookingForm}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: '1700',
      }}
    >
      <Grid
        container
        item
        // xs={12}
        sx={{
          backgroundColor: 'white',
          borderRadius: '8px',
          maxHeight: matches ? '500px' : '700px',
          position: 'relative',
          width: '40%',
          minWidth: '300px',
        }}
      >
        <Grid
          item
          xs={12}
          sx={{
            height: '70px',
            padding: '20px 20px 0px 20px',
            border: 'none',
            color: COLOR_PRIMARY_1,
            fontWeight: 'bold',
            //   boxShadow: '0 4px 12px #0002',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h5">{headingMapper[headingFor]}</Typography>
          <CloseIcon
            sx={{ cursor: 'pointer', color: COLOR_PRIMARY_1 }}
            onClick={closeBookingForm}
          />
        </Grid>
        <Grid
          container
          item
          xs={12}
          sx={{
            maxHeight: matches
              ? '330px'
              : showEmailNotEnteredWarning || showEnterValidEmailWarning
              ? '460px'
              : '530px',
            overflow: 'auto',
          }}
        >
          <Grid
            container
            item
            xs={12}
            sx={{
              padding: '20px 40px',
            }}
          >
            <Grid container item xs={12}>
              <Grid item xs={6} sx={{ paddingRight: '5px' }}>
                <TextField
                  id="outlined-basic"
                  label="First Name"
                  variant="outlined"
                  fullWidth
                  value={firstName}
                  onChange={e => {
                    setFirstName(e.target.value);
                  }}
                  //   sx={{
                  //     '& .MuiOutlinedInput-input': {
                  //       padding: '10px',
                  //     },
                  //   }}
                />
              </Grid>
              <Grid item xs={6} sx={{ paddingLeft: '5px' }}>
                <TextField
                  id="outlined-basic"
                  label="Last Name"
                  variant="outlined"
                  fullWidth
                  value={lastName}
                  onChange={e => {
                    setLastName(e.target.value);
                  }}
                  //   sx={{
                  //     '& .MuiOutlinedInput-input': {
                  //       padding: '10px',
                  //     },
                  //   }}
                />
              </Grid>
            </Grid>
            <Grid item xs={12} sx={{ marginTop: '15px' }}>
              <TextField
                id="outlined-basic"
                label="Company/Individual Use"
                variant="outlined"
                fullWidth
                value={companyName}
                onChange={e => {
                  setCompanyName(e.target.value);
                }}
                // sx={{
                //   '& .MuiOutlinedInput-input': {
                //     padding: '10px',
                //   },
                // }}
              />
            </Grid>
            <Grid container item xs={12} sx={{ marginTop: '15px' }}>
              <Grid item xs={6} sx={{ paddingRight: '5px' }}>
                <TextField
                  id="outlined-basic"
                  label="Email"
                  variant="outlined"
                  fullWidth
                  value={email}
                  onChange={e => {
                    setEmail(e.target.value);
                  }}
                  //   sx={{
                  //     '& .MuiOutlinedInput-input': {
                  //       padding: '10px',
                  //     },
                  //   }}
                />
              </Grid>
              <Grid item xs={6} sx={{ paddingLeft: '5px' }}>
                <TextField
                  id="outlined-basic"
                  label="Phone Number"
                  variant="outlined"
                  fullWidth
                  value={phoneNumber}
                  onChange={e => {
                    setPhoneNumber(e.target.value);
                  }}
                  //   sx={{
                  //     '& .MuiOutlinedInput-input': {
                  //       padding: '10px',
                  //     },
                  //   }}
                />
              </Grid>
            </Grid>
            <Grid item xs={12} sx={{ marginTop: '15px' }}>
              <TextField
                id="outlined-basic"
                label="Markets (List countries if you need nationwide data)"
                variant="outlined"
                fullWidth
                value={markets}
                onChange={e => {
                  setMarkets(e.target.value);
                }}
                // sx={{
                //   '& .MuiOutlinedInput-input': {
                //     padding: '5px',
                //   },
                // }}
              />
            </Grid>
            <Grid item xs={12} sx={{ marginTop: '15px' }}>
              <TextField
                id="outlined-multiline-static"
                label="What are you looking to accomplish?"
                variant="outlined"
                fullWidth
                multiline
                value={additionNote}
                onChange={e => {
                  setAdditionalNote(e.target.value);
                }}
                rows={4}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            height: showEmailNotEnteredWarning || showEnterValidEmailWarning ? '110px' : '60px',
            padding: '10px',
          }}
        >
          <Button
            sx={{
              width: '100%',
              backgroundColor: COLOR_PRIMARY_1,
              color: SIDEBAR_COLOR,
              borderRadius: '10px',
              '&:hover': { backgroundColor: COLOR_PRIMARY_1, color: SIDEBAR_COLOR },
            }}
            onClick={handleOnContactSalesBtn}
          >
            Get in Touch{' '}
            {submitLoading && (
              <CircularProgress sx={{ color: 'white', marginLeft: '4px' }} size={30} />
            )}
          </Button>
          {showEmailNotEnteredWarning && (
            <Box sx={{ margin: '10px 0px' }}>
              <Alert severity="warning" sx={{ padding: '0px 5px' }}>
                Please enter your email ID.
              </Alert>
            </Box>
          )}
          {showEnterValidEmailWarning && (
            <Box sx={{ margin: '10px 0px' }}>
              <Alert severity="error" sx={{ padding: '0px 5px' }}>
                Please enter a valid email ID.
              </Alert>
            </Box>
          )}
        </Grid>
      </Grid>
    </Modal>
  );
};

export default DemoBookingForm;
